<script setup lang="ts">
import { PhX } from '@phosphor-icons/vue'
import { computed, shallowRef } from 'vue'
import { formatNumber } from '@/utils/number'
import { buildImageResizeUrl, buildImageScaleUrl } from '@/utils/distribute/media-url'
import type { Emits, Props } from './type'

defineOptions({
  name: 'FollowCartoonSheet',
})

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  loadingMore: false,
  hasNext: false,
})
const emit = defineEmits<Emits>()

const skeletonItems = computed(() => Array.from({ length: 6 }, (_, index) => index))
const bodyElement = shallowRef<HTMLElement | null>(null)

function resolvePreviewCover(url: string | null | undefined) {
  return buildImageScaleUrl(url, { width: 464, height: 676 }) ?? url ?? ''
}

function resolveAvatarSrc(url: string | null | undefined) {
  return buildImageResizeUrl(url, { width: 264, height: 264, mode: 'fill' }) ?? url ?? ''
}

function handleBodyScroll() {
  const element = bodyElement.value

  if (!element || props.loadingMore || !props.hasNext) {
    return
  }

  const distanceToBottom = element.scrollHeight - element.scrollTop - element.clientHeight

  if (distanceToBottom <= 120) {
    emit('loadMore')
  }
}
</script>

<template>
  <div v-if="open" class="follow-sheet" role="dialog" aria-modal="true" aria-labelledby="follow-sheet-title">
    <button class="follow-sheet__backdrop" type="button" aria-label="关闭" @click="emit('close')" />

    <section class="follow-sheet__panel">
      <header class="follow-sheet__header">
        <h2 id="follow-sheet-title" class="follow-sheet__title">我的关注</h2>

        <button type="button" class="follow-sheet__close" aria-label="关闭" @click="emit('close')">
          <PhX :size="18" weight="bold" />
        </button>
      </header>

      <div ref="bodyElement" class="follow-sheet__body" @scroll="handleBodyScroll">
        <template v-if="loading && items.length === 0">
          <article
            v-for="item in skeletonItems"
            :key="item"
            class="follow-sheet__section is-skeleton"
            aria-hidden="true"
          >
            <div class="follow-sheet__hero-skeleton" />
            <div class="follow-sheet__line-skeleton is-title" />
            <div class="follow-sheet__line-skeleton" />
            <div class="follow-sheet__line-skeleton is-short" />
          </article>
        </template>

        <template v-else>
          <article
            v-for="item in items"
            :key="item.id"
            class="follow-sheet__section"
          >
            <button type="button" class="follow-sheet__hero" @click="emit('cartoonClick', item)">
              <img
                class="follow-sheet__avatar"
                :src="resolveAvatarSrc(item.avatar)"
                :alt="item.name"
              />
              <div class="follow-sheet__hero-copy">
                <div class="follow-sheet__headline">
                  <h3 class="follow-sheet__name">{{ item.name }}</h3>
                  <p class="follow-sheet__summary">{{ item.summary || '这个角色正在更新新的关注内容。' }}</p>
                </div>
                <p class="follow-sheet__meta">
                  <span class="follow-sheet__meta-group">
                    <span>{{ formatNumber(item.useNum) }}</span>
                    <span>互动与播放</span>
                  </span>
                  <span class="follow-sheet__meta-group">
                    <span>{{ formatNumber(item.fansNum) }}</span>
                    <span>关注</span>
                  </span>
                </p>
              </div>
            </button>

            <div v-if="item.list.length > 0" class="follow-sheet__preview-grid">
              <button
                v-for="work in item.list.slice(0, 3)"
                :key="work.id"
                type="button"
                class="follow-sheet__preview"
                @click="emit('workClick', work)"
              >
                <img :src="resolvePreviewCover(work.cover)" :alt="work.name" loading="lazy" decoding="async" />
                <span class="follow-sheet__preview-title">{{ work.name }}</span>
              </button>
            </div>

          </article>

          <div v-if="loadingMore" class="follow-sheet__loading-more">加载中...</div>
        </template>
      </div>
    </section>
  </div>
</template>

<style scoped>
.follow-sheet {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: grid;
  place-items: center;
  padding: 24px 12px;
  box-sizing: border-box;
}

.follow-sheet__backdrop {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgb(0 0 0 / 0.5);
}

.follow-sheet__panel {
  position: relative;
  display: flex;
  width: min(520px, calc(100vw - 24px));
  max-height: min(86vh, 693px);
  flex-direction: column;
  overflow: hidden;
  border-radius: 24px;
  background: var(--app-bg);
  box-shadow: var(--app-shadow-floating);
  animation: follow-sheet-enter 0.22s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.follow-sheet__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 8px;
}

.follow-sheet__title {
  margin: 0;
  color: var(--app-text-main);
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
}

.follow-sheet__close {
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--app-text-main);
}

.follow-sheet__close:hover,
.follow-sheet__close:focus-visible {
  color: var(--app-text-main);
}

.follow-sheet__body {
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: 0 0 20px;
}

.follow-sheet__section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
}

.follow-sheet__section.is-skeleton {
  padding-top: 16px;
  padding-bottom: 16px;
}

.follow-sheet__hero {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 16px;
  padding: 0;
  text-align: left;
  color: inherit;
}

.follow-sheet__avatar {
  width: 88px;
  height: 88px;
  flex: 0 0 auto;
  border-radius: 999px;
  object-fit: cover;
}

.follow-sheet__hero-copy {
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.follow-sheet__headline {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}

.follow-sheet__name {
  margin: 0;
  color: var(--app-text-main);
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
}

.follow-sheet__summary {
  overflow: hidden;
  margin: 0;
  color: var(--app-text-soft);
  font-size: 14px;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.follow-sheet__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin: 0;
  color: var(--app-text-muted);
  font-size: 14px;
  line-height: 20px;
}

.follow-sheet__meta-group {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.follow-sheet__preview-grid {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.follow-sheet__preview {
  position: relative;
  display: flex;
  aspect-ratio: 11 / 16;
  min-height: 0;
  align-items: flex-end;
  overflow: hidden;
  border-radius: 12px;
  padding: 10px;
  text-align: left;
  color: white;
  background: var(--app-surface-hover);
}

.follow-sheet__preview img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.follow-sheet__preview::after {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgb(0 0 0 / 0) 38%, rgb(0 0 0 / 0.5) 100%);
  transition: background 0.18s ease;
  content: '';
}

.follow-sheet__preview-title {
  position: relative;
  z-index: 1;
  display: -webkit-box;
  overflow: hidden;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  white-space: normal;
}

.follow-sheet__preview:hover::after,
.follow-sheet__preview:focus-visible::after {
  background:
    linear-gradient(180deg, rgb(0 0 0 / 0.08) 0%, rgb(0 0 0 / 0.08) 100%),
    linear-gradient(180deg, rgb(0 0 0 / 0) 38%, rgb(0 0 0 / 0.5) 100%);
}

.follow-sheet__loading-more {
  padding: 4px 20px 0;
  color: var(--app-text-muted);
  font-size: 13px;
  line-height: 18px;
  text-align: center;
}

.follow-sheet__hero-skeleton,
.follow-sheet__line-skeleton {
  position: relative;
  overflow: hidden;
  background: var(--app-skeleton-base);
}

.follow-sheet__hero-skeleton::before,
.follow-sheet__line-skeleton::before {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent 0%, var(--app-skeleton-shimmer) 50%, transparent 100%);
  animation: follow-sheet-shimmer 1.4s linear infinite;
  content: '';
}

.follow-sheet__hero-skeleton {
  height: 88px;
  border-radius: 999px;
}

.follow-sheet__line-skeleton {
  height: 12px;
  border-radius: 999px;
}

.follow-sheet__line-skeleton.is-title {
  width: 54%;
}

.follow-sheet__line-skeleton.is-short {
  width: 34%;
}

@keyframes follow-sheet-shimmer {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(100%);
  }
}

@keyframes follow-sheet-enter {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.985);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 900px) {
  .follow-sheet__panel {
    max-height: min(88vh, 693px);
  }
}

@media (max-width: 640px) {
  .follow-sheet {
    padding: 16px 12px;
  }

  .follow-sheet__header {
    padding: 18px 16px 8px;
  }

  .follow-sheet__body {
    padding: 0 0 16px;
  }

  .follow-sheet__hero {
    align-items: flex-start;
  }

  .follow-sheet__section {
    padding: 16px;
  }

  .follow-sheet__avatar {
    width: 72px;
    height: 72px;
  }

  .follow-sheet__hero-copy {
    gap: 8px;
  }

  .follow-sheet__summary,
  .follow-sheet__meta {
    font-size: 13px;
    line-height: 18px;
  }

  .follow-sheet__preview {
    border-radius: 12px;
  }
}
</style>
